// The phone's rear camera in the page (getUserMedia): the QR scan and the parking photo's viewfinder.

/** Long side of a captured photo: enough to judge the parking, light to keep and to send. */
export const MAX_SIDE = 1280;

/** Size of a capture from a `w` × `h` frame, scaled down to MAX_SIDE; undefined before a frame. */
export function captureSize(
  w: number,
  h: number,
  max = MAX_SIDE,
): { width: number; height: number } | undefined {
  if (!w || !h) return undefined;
  const k = Math.min(1, max / Math.max(w, h));
  return { width: Math.round(w * k), height: Math.round(h * k) };
}

/** getUserMedia needs a secure context (HTTPS or localhost); without it, the native camera. */
export function liveCameraAvailable(
  w: { isSecureContext: boolean; navigator: { mediaDevices?: Partial<MediaDevices> } } = window,
): boolean {
  return w.isSecureContext && typeof w.navigator.mediaDevices?.getUserMedia === "function";
}

/**
 * Opens the rear camera into `video` (asks permission the first time). `hd` asks for a 1080p
 * stream (photo); the scan keeps the default size, faster to decode. Throws when refused.
 */
export async function openRearCamera(
  video: HTMLVideoElement,
  hd = false,
): Promise<{ track: MediaStreamTrack | undefined; stop: () => void }> {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: {
      facingMode: "environment",
      ...(hd && { width: { ideal: 1920 }, height: { ideal: 1080 } }),
    },
    audio: false,
  });
  const stop = () => {
    for (const t of stream.getTracks()) t.stop();
  };
  try {
    video.srcObject = stream;
    await video.play();
  } catch (e) {
    stop();
    throw e;
  }
  return { track: stream.getVideoTracks()[0], stop };
}

/** The frame on screen as a JPEG (no EXIF: a canvas never carries the phone's GPS tags). */
export function captureFrame(video: HTMLVideoElement): Promise<Blob> {
  const size = captureSize(video.videoWidth, video.videoHeight);
  if (!size) return Promise.reject(new Error("no frame"));
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  canvas.getContext("2d")?.drawImage(video, 0, 0, size.width, size.height);
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("encode failed"))), "image/jpeg", 0.85),
  );
}
